import { useCallback, useEffect, useState } from 'react';

/**
 * useStatusCounts — global per-status counts for a KPI row.
 *
 * Generic over the lookup, so cases and registrations share one implementation.
 * The caller supplies `countFor(statusID)`, which is a `listView({ take: 1, statusID })`
 * call in both cases — one row is fetched purely to read `totalCount`, keeping the
 * counts global rather than scoped to whatever filter the page has applied.
 */

/** The subset of a status lookup this hook needs — every CCMS status lookup satisfies it. */
export interface StatusLookup {
  statusID: number;
  name?: string | null;
  color?: string | null;
  icon?: string | null;
}

export interface StatusCountRow extends StatusLookup {
  count: number;
}

export interface UseStatusCountsResult {
  rows: StatusCountRow[];
  allCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useStatusCounts(
  statuses: StatusLookup[],
  countFor: (statusID: number) => Promise<number>,
  countAll: () => Promise<number>,
): UseStatusCountsResult {
  const [rows, setRows] = useState<StatusCountRow[]>([]);
  const [allCount, setAllCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (statuses.length === 0) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [all, ...counts] = await Promise.all([
        countAll(),
        ...statuses.map((s) => countFor(s.statusID)),
      ]);
      setAllCount(all);
      setRows(statuses.map((s, i) => ({ ...s, count: counts[i] ?? 0 })));
    } catch (e) {
      console.error('Error fetching status counts:', e);
    } finally {
      setLoading(false);
    }
    // `countFor` / `countAll` are recreated per render by their callers, so they are
    // deliberately not deps — the status list is what actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statuses]);

  useEffect(() => { refresh(); }, [refresh]);

  return { rows, allCount, loading, refresh };
}

export default useStatusCounts;
